import { useEffect, useRef } from 'react';

const HISTORY_BUFFER_SIZE = 5;
const REPLENISH_COUNT = 3;

export function usePreventBackNavigation(
  enabled = true,
  allowNavigationRef?: React.RefObject<boolean>,
  onBackAttempt?: () => void
) {
  const onBackAttemptRef = useRef(onBackAttempt);
  onBackAttemptRef.current = onBackAttempt;

  useEffect(() => {
    if (!enabled) return;

    // Capture the URL at mount — used to restore if a stale buffer entry
    // from a previous page gets popped.
    const mountedUrl = window.location.href;

    // Layer 1: History buffer — works on all browsers.
    for (let i = 0; i < HISTORY_BUFFER_SIZE; i++) {
      window.history.pushState({ preventBack: true }, '', mountedUrl);
    }

    const handlePopState = () => {
      if (allowNavigationRef?.current) return;

      // If back popped to a stale entry from a different page, restore our URL.
      if (window.location.href !== mountedUrl) {
        window.history.replaceState({ preventBack: true }, '', mountedUrl);
      }

      for (let i = 0; i < REPLENISH_COUNT; i++) {
        window.history.pushState({ preventBack: true }, '', mountedUrl);
      }

      onBackAttemptRef.current?.();
    };

    window.addEventListener('popstate', handlePopState);

    // Layer 2: Navigation API (Chrome 102+ / Android PWA).
    let removeNavigateListener: (() => void) | undefined;
    if (typeof window.navigation !== 'undefined') {
      const handleNavigate = (event: NavigationNavigateEvent) => {
        if (event.navigationType !== 'traverse') return;
        if (allowNavigationRef?.current) return;
        if (!event.canIntercept) return;

        event.intercept({
          handler: async () => {
            /* no-op: cancel the traversal */
          },
        });
        onBackAttemptRef.current?.();
      };

      window.navigation.addEventListener('navigate', handleNavigate);
      removeNavigateListener = () => {
        window.navigation?.removeEventListener('navigate', handleNavigate);
      };
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
      removeNavigateListener?.();
    };
  }, [enabled, allowNavigationRef]);
}
